import { produce } from 'immer';
import { of } from 'rxjs';
import { mergeMap, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Action, NgxsOnInit, State, StateContext } from '@ngxs/store';

import { News, NewsRequest, NewsStatuses, NewsTag } from '../../models';
import { NewsService } from '../../services/news.service';
import { NewsAdminActions } from '../actions/news-admin.actions';

export interface NewsAdminStateModel {
  list: News[];
  statuses: NewsStatuses[];
  tags: NewsTag[];
  filters: {
    searchText: string | null;
    tags: string[];
    status: string | null;
    type: number | null;
  };
}

@State<NewsAdminStateModel>({
  name: 'newsAdmin',
  defaults: {
    list: [],
    statuses: [],
    tags: [],
    filters: {
      searchText: null,
      tags: [],
      status: null,
      type: 0,
    },
  },
})
@Injectable({
  providedIn: 'root',
})
export class NewsAdminState implements NgxsOnInit {
  constructor(private newsService: NewsService) {}

  ngxsOnInit(ctx: StateContext<NewsAdminStateModel>) {
    ctx.dispatch(NewsAdminActions.LoadStatuses);
    ctx.dispatch(NewsAdminActions.LoadTags);
  }

  @Action(NewsAdminActions.Load)
  public load({ patchState, getState }: StateContext<NewsAdminStateModel>) {
    const state = getState();
    const req = {
      title: state.filters.searchText,
      status: state.filters.status,
      tagIds: state.filters.tags,
    };
    return this.newsService.getNewsAdminWithFilters(req).pipe(
      tap(result => {
        patchState({
          list: result,
        });
      })
    );
  }

  @Action(NewsAdminActions.SetNewsAdminType)
  public setNewsAdminType(ctx: StateContext<NewsAdminStateModel>, { type }: NewsAdminActions.SetNewsAdminType) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.type = type;
      })
    );

    return ctx.dispatch(NewsAdminActions.ResetNewsAdminFilters);
  }

  @Action(NewsAdminActions.SetSearchText)
  public setSearchText(ctx: StateContext<NewsAdminStateModel>, { searchText }: NewsAdminActions.SetSearchText) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.searchText = searchText;
      })
    );

    return ctx.dispatch(new NewsAdminActions.Load());
  }

  @Action(NewsAdminActions.LoadStatuses)
  public loadStatuses({ patchState }: StateContext<NewsAdminStateModel>) {
    return this.newsService.getNewsStatuses().pipe(
      tap(result => {
        patchState({
          statuses: result,
        });
      })
    );
  }

  @Action(NewsAdminActions.SetStatuses)
  public setStatuses(ctx: StateContext<NewsAdminStateModel>, { statuses }: NewsAdminActions.SetStatuses) {
    ctx.patchState({
      statuses,
    });
  }

  @Action(NewsAdminActions.SetStatus)
  public setStatus(ctx: StateContext<NewsAdminStateModel>, { status }: NewsAdminActions.SetStatus) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.status = status;
      })
    );

    return ctx.dispatch(new NewsAdminActions.Load());
  }

  @Action(NewsAdminActions.LoadTags)
  public loadTags({ patchState }: StateContext<NewsAdminStateModel>) {
    return this.newsService.getNewsTags().pipe(
      tap(result => {
        patchState({
          tags: result,
        });
      })
    );
  }

  @Action(NewsAdminActions.CreateNewsAdmin)
  public createNewsAdmin(ctx: StateContext<NewsAdminStateModel>, { news, file }: NewsAdminActions.CreateNewsAdmin) {
    let newsId = '';
    return this.newsService.addNews(news).pipe(
      switchMap(response => {
        newsId = response.uuid;
        return this.newsService.uploadImage(file, newsId);
      }),
      mergeMap(() => ctx.dispatch([new NewsAdminActions.CreateNewsAdminSuccess(), new NewsAdminActions.Load()]))
    );
  }

  @Action(NewsAdminActions.UpdateNewsAdmin)
  public updateNewsAdmin(
    ctx: StateContext<NewsAdminStateModel>,
    { newsId, news, file }: NewsAdminActions.UpdateNewsAdmin
  ) {
    return this.newsService.updateNews(newsId, news).pipe(
      switchMap(response => {
        if (file.size) {
          return this.newsService.uploadImage(file, newsId);
        }
        return of({});
      }),
      mergeMap(() => ctx.dispatch([new NewsAdminActions.CreateNewsAdminSuccess(), new NewsAdminActions.Load()]))
    );
  }

  @Action(NewsAdminActions.ResetNewsAdminFilters)
  public resetNewsAdminFilters(ctx: StateContext<NewsAdminStateModel>) {
    ctx.patchState({
      filters: {
        searchText: null,
        tags: [],
        status: null,
        type: ctx.getState().filters.type,
      },
    });

    return ctx.dispatch(new NewsAdminActions.Load());
  }

  @Action(NewsAdminActions.DeleteNewsAdmin)
  public deleteNewsAdmin(ctx: StateContext<NewsAdminStateModel>, { news }: NewsAdminActions.DeleteNewsAdmin) {
    return this.newsService
      .updateNews(news.id, {
        ...news,
        isDeleted: true,
      } as unknown as NewsRequest)
      .pipe(mergeMap(() => ctx.dispatch(new NewsAdminActions.Load())));
  }

  @Action(NewsAdminActions.DeleteNewsPermanentlyAdmin)
  public deleteNewsPermanentlyAdmin(
    ctx: StateContext<NewsAdminStateModel>,
    { id }: NewsAdminActions.DeleteNewsPermanentlyAdmin
  ) {
    return this.newsService.deleteNews(id).pipe(mergeMap(() => ctx.dispatch(new NewsAdminActions.Load())));
  }

  @Action(NewsAdminActions.DuplicateNewsAdmin)
  public duplicateNewsAdmin(ctx: StateContext<NewsAdminStateModel>, { id }: NewsAdminActions.DuplicateNewsAdmin) {
    return this.newsService.duplicateNews(id).pipe(mergeMap(() => ctx.dispatch(new NewsAdminActions.Load())));
  }

  @Action(NewsAdminActions.SetSearchTags)
  public setSearchTags(ctx: StateContext<NewsAdminStateModel>, { tags }: NewsAdminActions.SetSearchTags) {
    ctx.patchState(
      produce(ctx.getState(), state => {
        state.filters.tags = tags;
      })
    );
    return ctx.dispatch(new NewsAdminActions.Load());
  }
}
